// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState, useEffect, useCallback } from 'react'
import { Loader2, Pill, Plus, Trash2, Search, Globe, Building2 } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

/**
 * D2 Drug Formulary — the tenant's drug list (global seed rows + tenant-added rows). The
 * recareplan_interval_days column is what the re-careplan / renewal engine reads to schedule
 * renewals. Reads return own + global; add/delete are admin-only (enforced server-side) and only
 * tenant-owned rows can be deleted (global seed rows are read-only here).
 */
type NewDrug = {
  name: string; ndc: string; brandName: string; strength: string; manufacturer: string
  route: string; category: string; drugClass: string; recareplanIntervalDays: string; requiredBaselabs: string
}
const EMPTY: NewDrug = { name: '', ndc: '', brandName: '', strength: '', manufacturer: '', route: '', category: '', drugClass: '', recareplanIntervalDays: '', requiredBaselabs: '' }

function labCount(raw: string | null): number {
  if (!raw) return 0
  try { const a = JSON.parse(raw); return Array.isArray(a) ? a.length : 0 } catch { return 0 }
}

// D-UI-2: a funnel on every column of the formulary list. Values are what the cell shows.
const FORMULARY_FUNNELS: FunnelColumn<api.CareRxFormularyRow>[] = [
  { key: 'drug', label: 'Drug', value: d => d.name },
  { key: 'strength', label: 'Strength', value: d => d.strength || '—' },
  { key: 'route', label: 'Route', value: d => d.route || '—' },
  { key: 'category', label: 'Category', value: d => d.category || '—' },
  { key: 'class', label: 'Class', value: d => d.drug_class || '—' },
  { key: 'renewal', label: 'Renewal (days)', value: d => d.recareplan_interval_days ?? '—' },
  { key: 'base_labs', label: 'Base labs', value: d => labCount(d.required_baselabs) || '—' },
  { key: 'scope', label: 'Scope', value: d => (d.is_global ? 'Global' : 'Tenant') },
]

export default function CareRxFormulary() {
  const [items, setItems] = useState<api.CareRxFormularyRow[]>([])
  const fx = useTableFunnels(items, FORMULARY_FUNNELS)
  // D1 (v2.1 item 4): the red-line worklist — suggestions only; Andrew's click classifies.
  const [rl, setRl] = useState<{ items: any[]; unconfirmed: number; confirmed: number; total: number } | null>(null)
  const [picked, setPicked] = useState<Record<string, boolean>>({})
  const loadRl = () => api.getCareRxNeedsClassification().then(setRl).catch(() => setRl(null))
  useEffect(() => { loadRl() }, [])
  const [facets, setFacets] = useState<{ categories: string[]; routes: string[]; drug_classes: string[] }>({ categories: [], routes: [], drug_classes: [] })
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState('')
  const [route, setRoute] = useState('')
  const [activeOnly, setActiveOnly] = useState(false)
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [draft, setDraft] = useState<NewDrug>(EMPTY)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true); setMsg('')
    try {
      const d = await api.listCareRxFormulary({
        search: search.trim() || undefined,
        category: category || undefined,
        route: route || undefined,
        active: activeOnly || undefined,
      })
      setItems(d.items ?? [])
    } catch (e: any) { setMsg(friendlyError(e)) } finally { setLoading(false) }
  }, [search, category, route, activeOnly])

  useEffect(() => { api.getCareRxFormularyFacets().then(setFacets).catch(() => {}) }, [])
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t) }, [load])

  const add = async () => {
    if (!draft.name.trim()) { setMsg('Drug name is required.'); return }
    setBusy(true); setMsg('')
    try {
      const labs = draft.requiredBaselabs.split(',').map(s => s.trim()).filter(Boolean)
      await api.createCareRxFormulary({
        name: draft.name.trim(),
        ndc: draft.ndc.trim() || undefined,
        brandName: draft.brandName.trim() || undefined,
        strength: draft.strength.trim() || undefined,
        manufacturer: draft.manufacturer.trim() || undefined,
        route: draft.route.trim() || undefined,
        category: draft.category.trim() || undefined,
        drugClass: draft.drugClass.trim() || undefined,
        recareplanIntervalDays: draft.recareplanIntervalDays.trim() ? Number(draft.recareplanIntervalDays) : undefined,
        requiredBaselabs: labs.length ? labs : undefined,
      })
      setDraft(EMPTY); setShowAdd(false)
      await load()
    } catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }

  const remove = async (row: api.CareRxFormularyRow) => {
    if (!confirm(`Remove "${row.name}" from your formulary?`)) return
    setBusy(true); setMsg('')
    try { await api.deleteCareRxFormulary(row.id); await load() }
    catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* cite: v2.1 Part 10 item 4 + Part 12 D1 — "Drug list rows carry D1 pre-suggestions,
          Andrew's click classifies into the three dispositions, bulk-confirm applies only to
          multi-selected rows and logs each". Suggestions NEVER write the disposition. */}
      {rl && rl.unconfirmed > 0 && (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <b style={{ fontSize: 15 }}>Red line — needs classification</b>
            <span style={{ fontSize: 12.5, color: 'var(--color-text-mute)' }}>{rl.confirmed} confirmed · <b style={{ color: '#b45309' }}>{rl.unconfirmed} awaiting your read</b> · {rl.total} in the book</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text-mute)', margin: '4px 0 8px' }}>
            Suggestions are advisory. Nothing is classified until you confirm it; an unconfirmed drug never defaults.
          </div>
          {['nurse_administered', 'dispense_only', 'excluded_not_carerx'].map(d => (
            <button key={d} style={{ ...sbtnF, marginRight: 6 }}
              disabled={Object.values(picked).every(v => !v)}
              onClick={async () => {
                const ids = Object.entries(picked).filter(([, v]) => v).map(([k]) => k)
                if (ids.length === 0) return
                try { await api.classifyCareRxDrugs(ids, d); setPicked({}); loadRl(); load() }
                catch (e: any) { window.alert(e?.body?.message || 'Could not classify.') }
              }}>Confirm selected as {d.replace(/_/g, ' ')}</button>
          ))}
          <div style={{ maxHeight: 280, overflowY: 'auto', marginTop: 8 }}>
            {rl.items.map(d0 => (
              <label key={d0.formulary_id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0', fontSize: 12.5 }}>
                <input type="checkbox" checked={!!picked[d0.formulary_id]}
                  onChange={e => setPicked(p0 => ({ ...p0, [d0.formulary_id]: e.target.checked }))} />
                <span style={{ minWidth: 180 }}>{d0.name}{d0.brand_name ? ` (${d0.brand_name})` : ''}</span>
                {d0.suggested_disposition
                  ? <span style={{ color: '#0e7490' }}>suggests <b>{d0.suggested_disposition.replace(/_/g, ' ')}</b> — {d0.suggested_reason}</span>
                  : <span style={{ color: 'var(--color-text-mute)' }}>{d0.suggested_reason}</span>}
              </label>
            ))}
          </div>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={cardTitle}><Pill size={16} /> Drug formulary</div>
        <button style={primaryBtn} onClick={() => setShowAdd(v => !v)}><Plus size={15} /> {showAdd ? 'Cancel' : 'Add drug'}</button>
      </div>
      <p style={{ ...muted, marginTop: -8 }}>
        Renewal interval drives the re-careplan engine. Global rows are shared defaults; you can add your own drugs.
      </p>

      {showAdd && (
        <div style={card}>
          <div style={{ ...cardTitle, marginBottom: 12 }}>New formulary drug</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <Fld label="Drug name *"><input style={input} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Actemra 200 Mg/10 Ml Vial" /></Fld>
            <Fld label="Brand name"><input style={input} value={draft.brandName} onChange={e => setDraft({ ...draft, brandName: e.target.value })} /></Fld>
            <Fld label="NDC"><input style={input} value={draft.ndc} onChange={e => setDraft({ ...draft, ndc: e.target.value })} /></Fld>
            <Fld label="Strength"><input style={input} value={draft.strength} onChange={e => setDraft({ ...draft, strength: e.target.value })} /></Fld>
            <Fld label="Manufacturer"><input style={input} value={draft.manufacturer} onChange={e => setDraft({ ...draft, manufacturer: e.target.value })} /></Fld>
            <Fld label="Route"><input style={input} value={draft.route} onChange={e => setDraft({ ...draft, route: e.target.value })} placeholder="IV infusion / Subcutaneous" /></Fld>
            <Fld label="Category"><input style={input} value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value })} placeholder="Biologic" /></Fld>
            <Fld label="Drug class"><input style={input} value={draft.drugClass} onChange={e => setDraft({ ...draft, drugClass: e.target.value })} placeholder="Anti-TNF" /></Fld>
            <Fld label="Renewal interval (days)"><input style={input} type="number" value={draft.recareplanIntervalDays} onChange={e => setDraft({ ...draft, recareplanIntervalDays: e.target.value })} placeholder="180" /></Fld>
            <Fld label="Required baseline labs (comma-separated)"><input style={input} value={draft.requiredBaselabs} onChange={e => setDraft({ ...draft, requiredBaselabs: e.target.value })} placeholder="CBC, CMP, TB screening" /></Fld>
          </div>
          <div style={{ marginTop: 12 }}>
            <button style={primaryBtn} disabled={busy} onClick={add}>{busy ? <Loader2 size={15} className="spin" /> : <Plus size={15} />} Add to formulary</button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180 }}>
          <Search size={14} style={{ position: 'absolute', left: 9, top: 10, color: 'var(--color-text-mute)' }} />
          <input style={{ ...input, paddingLeft: 30 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, brand, or NDC" />
        </div>
        <select style={{ ...input, maxWidth: 200 }} value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {facets.categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select style={{ ...input, maxWidth: 200 }} value={route} onChange={e => setRoute(e.target.value)}>
          <option value="">All routes</option>
          {facets.routes.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <label style={{ ...muted, gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} /> Active only
        </label>
      </div>

      {msg && <div style={{ fontSize: 13, color: '#b91c1c' }}>{msg}</div>}

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ ...muted, padding: 20 }}><Loader2 size={15} className="spin" /> Loading formulary…</div>
        ) : items.length === 0 ? (
          <div style={{ ...muted, padding: 20 }}>No drugs match these filters.</div>
        ) : (
          <>{fx.chips}
          <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Drug {fx.funnel('drug')}</th>
                <th style={th}>Strength {fx.funnel('strength')}</th>
                <th style={th}>Route {fx.funnel('route')}</th>
                <th style={th}>Category {fx.funnel('category')}</th>
                <th style={th}>Class {fx.funnel('class')}</th>
                <th style={th}>Renewal (days) {fx.funnel('renewal')}</th>
                <th style={th}>Base labs {fx.funnel('base_labs')}</th>
                <th style={th}>Scope {fx.funnel('scope')}</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {fx.rows.map(d => (
                <tr key={d.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td data-label="Drug" style={td}>
                    <div style={{ fontWeight: 600 }}>{d.name}</div>
                    {d.brand_name && <div style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>{d.brand_name}{d.ndc ? ` · ${d.ndc}` : ''}</div>}
                  </td>
                  <td data-label="Strength" style={td}>{d.strength || '—'}</td>
                  <td data-label="Route" style={td}>{d.route || '—'}</td>
                  <td data-label="Category" style={td}>{d.category || '—'}</td>
                  <td data-label="Class" style={td}>{d.drug_class || '—'}</td>
                  <td data-label="Renewal (days)" style={td}>{d.recareplan_interval_days ?? '—'}</td>
                  <td data-label="Base labs" style={td}>{labCount(d.required_baselabs) || '—'}</td>
                  <td data-label="Scope" style={td}>
                    {d.is_global
                      ? <span style={badge('var(--color-text-mute)')}><Globe size={11} /> Global</span>
                      : <span style={badge('var(--color-primary)')}><Building2 size={11} /> Tenant</span>}
                  </td>
                  <td data-label="" style={td}>
                    {!d.is_global && (
                      <button style={linkDanger} disabled={busy} onClick={() => remove(d)} title="Remove"><Trash2 size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></>
        )}
      </div>
      {!loading && items.length > 0 && <div style={muted}>{items.length} drug{items.length === 1 ? '' : 's'}</div>}
      <ProtocolDefEditor />
    </div>
  )
}

function Fld({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label style={lbl}>{label}</label>{children}</div>
}

const card: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 12, padding: 14, background: 'var(--color-surface)' }
const cardTitle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700 }
const lbl: React.CSSProperties = { display: 'block', fontSize: 11, color: 'var(--color-text-mute)', marginBottom: 3 }
const input: React.CSSProperties = { width: '100%', padding: '7px 9px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-surface)', color: 'var(--color-text)', boxSizing: 'border-box' }
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: .4, color: 'var(--color-text-mute)', borderBottom: '2px solid var(--color-border)' }
const td: React.CSSProperties = { padding: '9px 10px', fontSize: 13 }
const muted: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-mute)', display: 'flex', alignItems: 'center', gap: 6 }
const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const linkDanger: React.CSSProperties = { background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center' }
const badge = (color: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color, border: `1px solid ${color}`, borderRadius: 999, padding: '2px 8px' })


// ── D-CRX-19: ProtocolDef editor (config-as-data, mig 218; §13.1 templates from 221). Admin-only
//    writes; a bound admin editing a GLOBAL template gets a per-pharmacy copy (server-enforced). ──
function ProtocolDefEditor() {
  const [defs, setDefs] = useState<api.CareRxProtocolDef[]>([])
  const [sel, setSel] = useState<api.CareRxProtocolDef | null>(null)
  const [steps, setSteps] = useState(''); const [mon, setMon] = useState(''); const [re, setRe] = useState('')
  const [pLoading, setPLoading] = useState(true)
  const [pBusy, setPBusy] = useState(false)
  const [pMsg, setPMsg] = useState('')

  const load = useCallback(() => {
    setPLoading(true)
    api.listCareRxProtocolDefs().then(r => setDefs(r.items ?? [])).catch(() => setDefs([])).finally(() => setPLoading(false))
  }, [])
  useEffect(() => { load() }, [load])

  const pick = (d: api.CareRxProtocolDef) => {
    setSel(d); setPMsg('')
    setSteps(d.steps ?? ''); setMon(d.monitoring ?? ''); setRe(d.reinitiation_criteria ?? '')
  }
  const save = async () => {
    if (!sel) return
    setPBusy(true); setPMsg('')
    try {
      const r = await api.updateCareRxProtocolDef(sel.id, { steps: steps || undefined, monitoring: mon || undefined, reinitiation_criteria: re || undefined })
      setPMsg(r.copy_on_write ? `Saved as a pharmacy-specific copy (v1) — the global template is unchanged.` : `Saved (v${r.version}).`)
      setSel(null); load()
    } catch (e) { setPMsg((e as { body?: { error?: string; field?: string } }).body?.error === 'invalid_json' ? `Invalid JSON in ${(e as { body?: { field?: string } }).body?.field}` : (e as Error).message) }
    finally { setPBusy(false) }
  }

  const ta: React.CSSProperties = { width: '100%', minHeight: 90, fontFamily: 'ui-monospace, monospace', fontSize: 12, padding: 8, borderRadius: 6, border: '1px solid var(--color-border)', boxSizing: 'border-box', background: 'var(--color-surface)', color: 'var(--color-text)' }
  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
      <div style={{ fontWeight: 700, fontSize: 14 }}>Protocol definitions (§13.1 — config-as-data)</div>
      <div style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>Steps, monitoring cadence, and reinitiation criteria are clinical CONFIG. Edits are audited and versioned; editing a global template creates a pharmacy-specific copy. Clinical values are Andrew&apos;s to sign off (§14).</div>
      {pMsg && <div style={{ fontSize: 12.5 }}>{pMsg}</div>}
      {pLoading ? <div style={{ fontSize: 13, color: 'var(--color-text-mute)' }}>Loading…</div> : (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {defs.map(d => (
            <button key={d.id} onClick={() => pick(d)} style={{
              padding: '5px 12px', borderRadius: 999, fontSize: 12.5, cursor: 'pointer',
              border: '1px solid var(--color-border)', background: sel?.id === d.id ? 'var(--color-primary)' : 'var(--color-surface)', color: sel?.id === d.id ? '#fff' : 'var(--color-text)',
            }}>{d.code} <span style={{ opacity: .7 }}>v{d.version} · {d.scope}</span></button>
          ))}
        </div>
      )}
      {sel && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <b style={{ fontSize: 13 }}>{sel.name}</b>
          <label style={{ fontSize: 12 }}>Steps (JSON)</label><textarea value={steps} onChange={e => setSteps(e.target.value)} style={ta} />
          <label style={{ fontSize: 12 }}>Monitoring (JSON)</label><textarea value={mon} onChange={e => setMon(e.target.value)} style={ta} />
          <label style={{ fontSize: 12 }}>Reinitiation criteria (JSON)</label><textarea value={re} onChange={e => setRe(e.target.value)} style={ta} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button disabled={pBusy} onClick={save} style={{ fontSize: 12.5, fontWeight: 700, cursor: 'pointer', border: '1px solid var(--color-primary)', background: 'var(--color-primary)', color: '#fff', borderRadius: 6, padding: '6px 14px' }}>{pBusy ? 'Saving…' : 'Save (audited)'}</button>
            <button onClick={() => setSel(null)} style={{ fontSize: 12.5, cursor: 'pointer', border: '1px solid var(--color-border)', background: 'var(--color-surface)', borderRadius: 6, padding: '6px 14px' }}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}
const sbtnF: React.CSSProperties = { fontSize: 12, border: '1px solid var(--color-border)', borderRadius: 7, padding: '3px 10px', background: 'transparent', cursor: 'pointer' }
